/**
 * Persist Phase 4 hard-gate shadow snapshot onto an items row (no status change).
 */

import type { Json } from "@/lib/database.types";
import type { AdminPriceSuitabilityStatus } from "@/lib/server/adminPriceSuitability";
import {
  evaluateListingHardGates,
  MIN_LISTING_PHOTOS_FOR_HARD_GATE,
  type ListingHardGatesResult,
} from "@/lib/listing-lifecycle/listingHardGates";
import { buildListingGatePersistFields } from "@/lib/listing-lifecycle/listingQualityScore";
import type { ListingSpecAttributes } from "@/lib/listing/specRequirements";

export type ListingGateShadowPersistFields = {
  listing_gate_breakdown: Json;
  listing_quality_score: number;
};

export function buildListingGateShadowPersistFields(input: {
  photoCount: number;
  subcategoryId: string | null | undefined;
  attributes?: ListingSpecAttributes | null;
  priceSuitability?: AdminPriceSuitabilityStatus | null;
  title: string;
  description?: string | null;
  defects?: string | null;
  nowIso?: string;
}): {
  fields: ListingGateShadowPersistFields;
  result: ListingHardGatesResult;
} {
  const result = evaluateListingHardGates({
    photoCount: input.photoCount,
    subcategoryId: input.subcategoryId,
    attributes: input.attributes,
    priceSuitability: input.priceSuitability ?? null,
    title: input.title,
    description: input.description,
    defects: input.defects,
    nowIso: input.nowIso,
  });

  const persist = buildListingGatePersistFields(result);
  return {
    result,
    fields: {
      listing_gate_breakdown: persist.listing_gate_breakdown as unknown as Json,
      listing_quality_score: persist.listing_quality_score,
    },
  };
}

/** At create time photos are contract-validated before insert (exactly 3). */
export function declaredCreatePhotoCountForHardGate(): number {
  return MIN_LISTING_PHOTOS_FOR_HARD_GATE;
}
